import type { InsightDrillTarget } from '../../../data/ServerApiClient'

// Shared CFO Core section navigation. Extracted verbatim from
// `CfoHomeBrief.tsx` (the new-surface foundation) so the unified shell, the
// Brief, and every interior page speak ONE navigation vocabulary. The rail is a
// "glance -> detail -> deep dive" grouping (Understand / Plan / Share /
// Advanced) with the Brief itself as the front door. It does NOT introduce a
// parallel navigation system — every item maps onto the canonical drill-target
// vocabulary (`NAV_DRILL_TARGETS`), the same path the insight cards and guided
// journeys already use.

export type CfoNavItem = { id: string; label: string; icon: string; sub?: boolean }
export type CfoNavSection = { id: string; label?: string; items: CfoNavItem[] }
export type CfoNavProfile = 'full' | 'lightweight'
export type CfoNavAvailability = {
  /** An active, verified Job finance publication contains at least one job. */
  jobCosting?: boolean
  /**
   * Investor readiness is a Pro module. PERMISSIVE by default, unlike `jobCosting`: only an explicit
   * `false` hides it, because the value comes from `cfoProFeaturesAvailable`, which reads a missing
   * plan payload as available. A bootstrap that failed or has not landed must not remove a module
   * from the rail — the page carries its own upgrade notice, and the server carries the real gate.
   */
  qoe?: boolean
}

export const NAV_SECTIONS: CfoNavSection[] = [
  {
    id: 'ceo',
    items: [
      { id: 'ceo', label: 'CEO · This morning', icon: '\u2600' },
      { id: 'ceo_analysis', label: 'CEO Analysis', icon: '\u25A3' },
    ],
  },
  { id: 'home', items: [{ id: 'home', label: 'Home · Brief', icon: '\u25C7' }] },
  {
    id: 'understand',
    label: 'Understand',
    items: [
      { id: 'financials', label: 'Financials', icon: '\u25A4' },
      { id: 'close', label: 'Close', icon: '\u2713' },
      { id: 'job_costing', label: 'Job finance', icon: '\u25A6' },
      { id: 'ratios', label: 'Ratios & trends', icon: '\u25D4' },
      { id: 'ar', label: 'A/R & collectability', icon: '\u25F7' },
    ],
  },
  {
    id: 'explore',
    label: 'Explore',
    items: [
      { id: 'gl_detail', label: 'GL detail', icon: '\u25A4' },
      { id: 'explore', label: 'Viz & sheets', icon: '\u2913' },
    ],
  },
  {
    id: 'plan',
    label: 'Plan',
    items: [
      { id: 'cash', label: 'Cash \u00B7 13 weeks', icon: '\u25A5' },
      { id: 'forecast', label: 'Forecast & budget', icon: '\u25C8' },
      { id: 'valuation', label: 'Valuation', icon: '$' },
      // One nav item, three densities inside it. The rail is the owner's front door, so it carries
      // the owner-facing name; "Quality of earnings" is what the workpapers call themselves.
      { id: 'qoe', label: 'Investor readiness', icon: '\u25C7' },
    ],
  },
  {
    id: 'share',
    label: 'Share',
    items: [{ id: 'presentations', label: 'Presentations', icon: '\u25A6' }],
  },
  {
    id: 'tuning',
    label: 'AI',
    items: [{ id: 'analysis_profile', label: 'Context & Rules', icon: '\u2726' }],
  },
  {
    id: 'advanced',
    label: 'Advanced',
    items: [
      { id: 'layout', label: 'Layout & structure', icon: '', sub: true },
      { id: 'adjustments', label: 'Adjustments / JEs', icon: '', sub: true },
      { id: 'settings', label: 'Settings', icon: '', sub: true },
    ],
  },
]

export const LIGHTWEIGHT_NAV_SECTIONS: CfoNavSection[] = NAV_SECTIONS
  .map((section) => ({
    ...section,
    items: section.items.filter((item) => (
      item.id === 'ceo' ||
      item.id === 'ceo_analysis' ||
      item.id === 'home' ||
      item.id === 'financials' ||
      item.id === 'close' ||
      item.id === 'job_costing' ||
      item.id === 'ratios' ||
      item.id === 'ar' ||
      item.id === 'analysis_profile' ||
      item.id === 'cash' ||
      item.id === 'forecast' ||
      item.id === 'valuation' ||
      item.id === 'qoe' ||
      item.id === 'presentations' ||
      item.id === 'gl_detail' ||
      item.id === 'settings'
    )),
  }))
  .filter((section) => section.items.length > 0)

export const cfoNavSectionsForProfile = (
  profile: CfoNavProfile = 'full',
  availability: CfoNavAvailability = {},
): CfoNavSection[] => (profile === 'lightweight' ? LIGHTWEIGHT_NAV_SECTIONS : NAV_SECTIONS)
  .map((section) => ({
    ...section,
    items: section.items
      .filter((item) => item.id !== 'job_costing' || availability.jobCosting === true)
      .filter((item) => item.id !== 'qoe' || availability.qoe !== false),
  }))
  .filter((section) => section.items.length > 0)

// Maps each rail item to the canonical CFO Core drill target consumed by
// `onOpenCfoCore`. Tab keys match the insight spine's `drill_target.tab`
// vocabulary so the rail, the insight cards, and the server detectors all speak
// one language.
export const NAV_DRILL_TARGETS: Record<string, InsightDrillTarget> = {
  ceo: { tab: 'ceo', view: null, params: {} },
  ceo_analysis: { tab: 'ceo_analysis', view: null, params: {} },
  financials: { tab: 'financials', view: null, params: {} },
  close: { tab: 'close', view: null, params: {} },
  // `guided` is the default front door; `workspace` is the eight diligence tabs. Both are the same
  // page and the same engagement — the view only decides how much of it is shown at once.
  qoe: { tab: 'qoe', view: 'guided', params: {} },
  qoe_workspace: { tab: 'qoe', view: 'workspace', params: {} },
  job_costing: { tab: 'job_costing', view: null, params: {} },
  ratios: { tab: 'ratios_valuation', view: 'ratios', params: {} },
  cash: { tab: 'cash', view: null, params: {} },
  ar: { tab: 'ar', view: null, params: {} },
  analysis_profile: { tab: 'analysis_profile', view: null, params: {} },
  forecast: { tab: 'forecast', view: null, params: {} },
  scenarios: { tab: 'forecast', view: 'scenarios', params: {} },
  valuation: { tab: 'ratios_valuation', view: 'valuation', params: {} },
  explore: { tab: 'explore', view: null, params: {} },
  layout: { tab: 'layout', view: null, params: {} },
  adjustments: { tab: 'adjustments', view: null, params: {} },
  gl_detail: { tab: 'gl_detail', view: null, params: {} },
  settings: { tab: 'advanced', view: 'settings', params: {} },
}

// Interior pages that render natively INSIDE the unified shell today. Sections
// not in this set fall through to the `openClassicView` escape hatch (the
// untouched legacy `cfo-core` tab) until their calm page lands.
export type CfoPageId =
  | 'ceo'
  | 'ceo_analysis'
  | 'home'
  | 'financials'
  | 'close'
  | 'qoe'
  | 'job_costing'
  | 'cash'
  | 'ratios'
  | 'ar'
  | 'analysis_profile'
  | 'forecast'
  | 'scenarios'
  | 'valuation'
  | 'presentations'
  | 'explore'
  | 'gl_detail'
  | 'advanced'

export const IN_SHELL_PAGE_IDS: readonly CfoPageId[] = [
  'ceo',
  'ceo_analysis',
  'home',
  'financials',
  'close',
  'qoe',
  'job_costing',
  'cash',
  'ratios',
  'ar',
  'analysis_profile',
  'forecast',
  'scenarios',
  'valuation',
  'presentations',
  'explore',
  'gl_detail',
  'advanced',
]

export type CfoPageRoute = {
  pageId: CfoPageId
  /** Sub-view within a page (e.g. ratios vs valuation). */
  view: string | null
  /** Exact metric a drill action should reveal on the destination page. */
  focusMetricId?: string | null
}

export const isInShellPage = (value: unknown): value is CfoPageId =>
  typeof value === 'string' && (IN_SHELL_PAGE_IDS as readonly string[]).includes(value)

// Resolve a nav item id (rail click) to an in-shell page route, or null when the
// section is not yet a native page (caller uses the classic-view escape hatch).
export const resolveCfoPageFromNavId = (navId: string): CfoPageRoute | null => {
  switch (navId) {
    case 'ceo':
      return { pageId: 'ceo', view: null }
    case 'ceo_analysis':
      return { pageId: 'ceo_analysis', view: null }
    case 'home':
      return { pageId: 'home', view: null }
    case 'financials':
      return { pageId: 'financials', view: null }
    case 'close':
      return { pageId: 'close', view: null }
    case 'qoe':
      return { pageId: 'qoe', view: 'guided' }
    case 'qoe_workspace':
      return { pageId: 'qoe', view: 'workspace' }
    case 'job_costing':
    case 'job-costing':
      return { pageId: 'job_costing', view: null }
    case 'cash':
      return { pageId: 'cash', view: null }
    case 'ratios':
      return { pageId: 'ratios', view: 'ratios' }
    case 'ar':
      return { pageId: 'ar', view: null }
    case 'analysis_profile':
      return { pageId: 'analysis_profile', view: null }
    case 'forecast':
      return { pageId: 'forecast', view: null }
    case 'scenarios':
      return { pageId: 'scenarios', view: null }
    case 'valuation':
      return { pageId: 'valuation', view: 'valuation' }
    case 'presentations':
      return { pageId: 'presentations', view: null }
    case 'explore':
      return { pageId: 'explore', view: null }
    case 'gl_detail':
      return { pageId: 'gl_detail', view: null }
    // Advanced power tools land on the calm Advanced page; the
    // page itself escape-hatches into the specific classic legacy surface.
    case 'layout':
    case 'adjustments':
      return { pageId: 'advanced', view: null }
    case 'settings':
      return { pageId: 'advanced', view: 'settings' }
    default:
      return null
  }
}

// Resolve a canonical `InsightDrillTarget` (from an insight card or the agent)
// to an in-shell page route. Consumes `tab` + `view` so e.g. `ratios_valuation`
// with `view:'ratios'` routes to the Ratios page while `view:'valuation'` is
// left to the escape hatch in this slice.
export const resolveCfoPageFromDrillTarget = (
  target: InsightDrillTarget | null | undefined,
): CfoPageRoute | null => {
  if (!target) return null
  const tab = String(target.tab || '').trim()
  const view = target.view ? String(target.view).trim() : null
  const focusMetricId = String(target.params?.ratio || target.params?.metric || '').trim()
  switch (tab) {
    case 'ceo':
      return { pageId: 'ceo', view: view || null }
    case 'ceo_analysis':
      return { pageId: 'ceo_analysis', view: null }
    case 'home':
      return { pageId: 'home', view: null }
    case 'financials':
      return { pageId: 'financials', view: view || null }
    case 'close':
      return { pageId: 'close', view: view || null }
    case 'qoe':
      // An unrecognised view falls back to guided, never to the tabs: a drill target that means to
      // show an owner something must not drop them into the workpapers.
      return { pageId: 'qoe', view: view === 'workspace' || view === 'workpapers' ? 'workspace' : 'guided' }
    case 'job_costing':
    case 'job-costing':
      return { pageId: 'job_costing', view: view || null }
    case 'cash':
      return { pageId: 'cash', view: null }
    case 'ar':
      return { pageId: 'ar', view: view || null }
    case 'analysis_profile':
      return { pageId: 'analysis_profile', view: null }
    case 'forecast':
      // The scenarios sub-view is its own calm page; everything else is Forecast.
      return view === 'scenarios'
        ? { pageId: 'scenarios', view: null }
        : { pageId: 'forecast', view: view || null }
    case 'ratios_valuation':
      // Consume the sub-view to split the Ratios and Valuation pages.
      return view === 'valuation'
        ? { pageId: 'valuation', view: 'valuation' }
        : {
            pageId: 'ratios',
            view: 'ratios',
            ...(focusMetricId ? { focusMetricId } : {}),
          }
    case 'explore':
      return { pageId: 'explore', view: null }
    case 'gl_detail':
      return { pageId: 'gl_detail', view: null }
    case 'layout':
    case 'adjustments':
      return { pageId: 'advanced', view: null }
    case 'advanced':
      return view === 'settings'
        ? { pageId: 'advanced', view: 'settings' }
        : { pageId: 'advanced', view: null }
    default:
      return null
  }
}

// Maps an in-shell page id back to the nav item that should render active.
export const navIdForPage = (pageId: CfoPageId): string => pageId
